/**
 * Ledger, read-only (WP2-LEDGER-RO, BUILD-PLAN M21 wave 2). Replaces the
 * shell's Billing placeholder at `/billing` with the family ledger, aged
 * receivables and the PMS reconciliation view, per office and All offices.
 * WP4-BILLING adds payments and statements under `/billing/*`.
 *
 * Deep links: `/billing?tab=ledger&account=<guarantor or member id>`,
 * `/billing?tab=aging`, `/billing?tab=reconcile`; a patient's Account is
 * `/patients/<id>/account`.
 *
 * Money verbs (`ledger.enterPayment`, `ledger.enterAdjustment`,
 * `ledger.enterProcedure`, `ledger.statement`, `ledger.insurancePayment`,
 * `checkout.walkout`, `ledger.fastCheckout`) are registered by both pages
 * and by mode windows that show an account (`useMoneyVerbs`).
 */
import { defineManifest } from '@velora/contracts';
import { defineWebModule } from '../../app/module.js';
import { installLedgerSearch } from './search-provider.js';

installLedgerSearch();

export default defineWebModule({
  manifest: defineManifest({
    id: 'ledger',
    title: 'Billing',
    version: '0.1.0',
    owner: 'WP2-LEDGER-RO',
    entitlementKey: null,
    permissions: ['ledger.view', 'money.view'],
    routes: [
      {
        id: 'billing',
        path: '/billing',
        title: 'Billing',
        officeScope: 'location-or-all',
        permission: 'ledger.view',
        readsPhi: true,
      },
      {
        // A patient's Account (requests-opendental R5a): the household's
        // family ledger. The shell hosts the `ledger.page` slot around it.
        id: 'account',
        path: '/patients/$patientId/account',
        title: 'Account',
        officeScope: 'location-or-all',
        permission: 'ledger.view',
        readsPhi: true,
      },
    ],
    nav: [
      {
        label: 'Billing',
        labelKey: 'ledger',
        group: 'revenue',
        routeId: 'billing',
        icon: 'wallet',
        order: 10,
        permission: 'ledger.view',
      },
    ],
    commands: [
      {
        id: 'ledger.open',
        title: 'Go to Billing',
        keywords: ['ledger', 'balance', 'aging', 'family', 'account', 'statement'],
        routeId: 'billing',
        permission: 'ledger.view',
      },
    ],
    shortcuts: [{ keys: 'g l', description: 'Go to Billing (ledger)', routeId: 'billing' }],
    sseEntityKeys: ['ledger_entry', 'claim', 'aging_snapshot'],
    phiReadPoints: ['ledger.accounts', 'ledger.open', 'ledger.reconciliation'],
  }),
  pages: {
    billing: () => import('./BillingPage.js'),
    account: () => import('./AccountPage.js'),
  },
});
